import { useMemo } from "react";
import PropTypes from "prop-types";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

function toCalendarInputValue(value, fallbackDate) {
  const raw = value || fallbackDate;
  if (!raw && raw !== 0) return "";
  if (typeof raw === "string") {
    const normalized = raw.replace(" ", "T");
    return normalized.slice(0, 16);
  }
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toCalendarDateValue(value, fallbackDate) {
  const input = toCalendarInputValue(value, fallbackDate);
  return input ? input.slice(0, 10) : "";
}

function addMinutesToInputValue(inputValue, minutes) {
  if (!inputValue) return "";
  const [date = "", time = ""] = inputValue.split("T");
  const [year, month, day] = date.split("-").map(Number);
  const [hour = 0, minute = 0] = time.split(":").map(Number);
  if (!year || !month || !day) return "";

  const localDate = new Date(year, month - 1, day, hour, minute + minutes, 0, 0);
  return toCalendarInputValue(null, localDate);
}

function todayInputDate(timeZone) {
  if (timeZone && timeZone !== "local") {
    try {
      const parts = new Intl.DateTimeFormat("en", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(new Date());
      const get = (type) => parts.find((part) => part.type === type)?.value;
      return `${get("year")}-${get("month")}-${get("day")}`;
    } catch {
      // Si la zona configurada no es válida, caemos a la fecha local del navegador.
    }
  }
  return toCalendarInputValue(null, new Date()).slice(0, 10);
}

function isBeforeToday(inputValue, timeZone) {
  return !!inputValue && inputValue.slice(0, 10) < todayInputDate(timeZone);
}

function warnPastSelection() {
  window.alert("No se puede crear o mover una cita a una fecha anterior a hoy.");
}

// Mapea tus citas a eventos de FullCalendar
function mapAppointmentsToEvents(items = []) {
  return items
    .filter((a) => a.start_at || a.date)
    .map((a) => {
      const id = String(a.id ?? a.uuid ?? a._id ?? Math.random());
      const start = a.start_at || a.date;
      const end = a.end_at || null;

      const title =
        a.subject_name ||
        a.patient_name ||
        a.client_name ||
        a.service_name ||
        a.resource_names ||
        a.title ||
        `Cita #${id}`;

      return {
        id,
        title,
        start,
        end,
        extendedProps: {
          _appointment: a,
          status: a.status || "",
        },
      };
    });
}

export default function AppointmentCalendar({
  items = [],
  onSelect,
  onCreate,
  onMove,
  timeZone = "local",
}) {
  const events = useMemo(() => mapAppointmentsToEvents(items), [items]);

  const addMinutes = (date, minutes) => new Date(date.getTime() + minutes * 60 * 1000);

  // Para construir una fecha local a las 09:00 en vista month
  const buildDayWithHour = (date, hour = 9, minute = 0) => {
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();
    return new Date(year, month, day, hour, minute, 0, 0);
  };

  const handleDateClick = (info) => {
    try {
      const clickedDate = info.date instanceof Date ? info.date : new Date(info.date);
  
      // Si la vista month no trae hora, construimos a las 09:00
      const start = info.dateStr && info.dateStr.includes("T")
        ? clickedDate
        : buildDayWithHour(clickedDate, 9, 0);
  
      const end = addMinutes(start, 30);
      const startInput = toCalendarInputValue(info.dateStr, start);
      if (isBeforeToday(startInput, timeZone)) {
        warnPastSelection();
        return;
      }
  
      const slotPayload = {
        date: clickedDate,
        dateStr: toCalendarDateValue(info.dateStr, clickedDate),
        start: startInput,
        end: addMinutesToInputValue(startInput, 30) || toCalendarInputValue(info.endStr, end),
      };
  
      if (onCreate && typeof onCreate === "function") {
        onCreate(slotPayload);
      }
    } catch (err) {
      console.error("handleDateClick error:", err);
    }
  };

  // Clic en evento -> ver detalle
  const handleEventClick = (info) => {
    // intentamos obtener appointment desde extendedProps primero
    const appt =
      info.event.extendedProps?._appointment ||
      items.find((x) => String(x.id) === String(info.event.id));

    if (onSelect && appt) {
      onSelect(appt);
    }
  };

  // Drag & drop -> mover cita
  const handleEventDrop = (info) => {
    const appt = info.event.extendedProps?._appointment;
    const payload = {
      appointment: appt,
      start: toCalendarInputValue(info.event.startStr, info.event.start),
      end: toCalendarInputValue(info.event.endStr, info.event.end),
      revert: info.revert,
    };

    if (isBeforeToday(payload.start, timeZone)) {
      warnPastSelection();
      info.revert();
      return;
    }

    if (onMove && appt) {
      onMove(payload);
    } else {
      // si no hay handler, revertimos movimiento
      info.revert();
    }
  };

  const handleEventResize = (info) => {
    const appt = info.event.extendedProps?._appointment;
    const payload = {
      appointment: appt,
      start: toCalendarInputValue(info.event.startStr, info.event.start),
      end: toCalendarInputValue(info.event.endStr, info.event.end),
      revert: info.revert,
    };

    if (isBeforeToday(payload.start, timeZone)) {
      warnPastSelection();
      info.revert();
      return;
    }

    if (onMove && appt) {
      onMove(payload);
    } else {
      info.revert();
    }
  };

  return (
    <div className="appointments-calendar-wrapper relative">
      <FullCalendar
        locale="es"
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="timeGridWeek"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        height="auto"
        timeZone={timeZone}
        events={events}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        editable={true}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        selectable={true}
        select={(selectInfo) => {
          // selectInfo.start, selectInfo.end son Date objects
          if (onCreate) {
            const startInput = toCalendarInputValue(selectInfo.startStr, selectInfo.start);
            if (isBeforeToday(startInput, timeZone)) {
              warnPastSelection();
              return;
            }
            onCreate({
              date: selectInfo.start,
              dateStr: toCalendarDateValue(selectInfo.startStr, selectInfo.start),
              start: startInput,
              end:
                toCalendarInputValue(selectInfo.endStr, null) ||
                addMinutesToInputValue(startInput, 30) ||
                toCalendarInputValue(null, selectInfo.end),
            });
          }
        }}
        selectMirror={true}
        dayMaxEvents={3}
        slotMinTime="07:00:00"
        slotMaxTime="21:00:00"
      />

      <p className="mt-1 text-[9px] text-[color:var(--muted)]">
        Haz clic en un espacio vacío para crear una cita rápida. Arrastra una
        cita para moverla de día u hora.
      </p>
    </div>
  );
}

AppointmentCalendar.propTypes = {
  items: PropTypes.array,
  onSelect: PropTypes.func,
  onCreate: PropTypes.func,
  onMove: PropTypes.func,
  timeZone: PropTypes.string,
};
